import { useEffect, useState } from 'react';
import { Lock, MessageCircle, LogOut, Pause, Archive, CreditCard, UserX } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import type { AccessReason } from '@/hooks/useClientAccess';

interface Props {
  reason: AccessReason;
  coachId: string | null;
  pauseUntil: string | null;
  pauseReason: string | null;
}

interface CoachInfo {
  name: string;
  phone: string;
}

/**
 * Lock screen shown to a client when their access is blocked
 * (paused / archived / payment due / no coach assigned).
 *
 * One screen, four reason-driven variants — copy + icon + accent colour
 * change but the structure (lock + message + contact-coach button +
 * sign-out) stays consistent. The client gets enough info to act
 * (contact the coach) without being able to navigate to anything else.
 */
export function AccessBlockedScreen({ reason, coachId, pauseUntil, pauseReason }: Props) {
  const [coach, setCoach] = useState<CoachInfo | null>(null);

  useEffect(() => {
    if (!coachId) return;
    (async () => {
      const [{ data: ob }, { data: prof }] = await Promise.all([
        supabase.from('client_onboarding_profiles')
          .select('full_name, phone')
          .eq('user_id', coachId).maybeSingle(),
        supabase.from('profiles')
          .select('display_name')
          .eq('id', coachId).maybeSingle(),
      ]);
      const name = ob?.full_name || prof?.display_name || 'tu coach';
      const phone = (ob?.phone || '').replace(/\D/g, '');
      setCoach({ name, phone });
    })();
  }, [coachId]);

  const variant = (() => {
    switch (reason) {
      case 'paused':
        return {
          Icon: Pause,
          title: 'Tu plan está en pausa',
          tone: 'amber',
          body: pauseUntil
            ? `${coach?.name || 'Tu coach'} pausó tu plan hasta el ${formatDate(pauseUntil)}.`
            : `${coach?.name || 'Tu coach'} pausó tu plan.`,
          subBody: pauseReason
            ? `Motivo: ${pauseReason}`
            : 'Cuando termine la pausa volverás a tener acceso completo.',
          cta: 'Si quieres reactivar antes, contáctalo.',
        };
      case 'archived':
        return {
          Icon: Archive,
          title: 'Tu cuenta fue archivada',
          tone: 'red',
          body: `${coach?.name || 'Tu coach'} archivó tu cuenta.`,
          subBody: 'Tu historial sigue guardado. Contacta a tu coach si quieres volver.',
          cta: 'Contactar a tu coach',
        };
      case 'payment_due':
      case 'payment_overdue':
        return {
          Icon: CreditCard,
          title: 'Pago pendiente',
          tone: 'amber',
          body: 'Tu acceso a la app está pausado por un pago pendiente.',
          subBody: 'En cuanto tu coach marque tu pago, recuperas el acceso.',
          cta: 'Contacta a tu coach',
        };
      case 'no_coach':
      default:
        return {
          Icon: UserX,
          title: 'Sin coach asignado',
          tone: 'muted',
          body: 'No tienes ningún coach activo en este momento.',
          subBody: 'Contacta al equipo si crees que esto es un error.',
          cta: null,
        };
    }
  })();

  const toneClasses =
    variant.tone === 'red' ? 'border-red-500/40 bg-red-500/5' :
    variant.tone === 'amber' ? 'border-amber-500/40 bg-amber-500/5' :
    'border-border bg-secondary/30';
  const iconBg =
    variant.tone === 'red' ? 'bg-red-500/15 text-red-500 dark:text-red-400' :
    variant.tone === 'amber' ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400' :
    'bg-muted text-muted-foreground';

  const handleContact = () => {
    if (!coach?.phone) {
      const text = encodeURIComponent(
        `Hola ${coach?.name || 'coach'}, no puedo entrar a la app. ¿Podrías ayudarme?`,
      );
      window.location.href = `https://wa.me/?text=${text}`;
      return;
    }
    const text = encodeURIComponent(
      reason === 'paused'
        ? `Hola ${coach.name}, mi plan está pausado. ¿Podemos hablar?`
        : reason === 'archived'
          ? `Hola ${coach.name}, mi cuenta está archivada. Me gustaría volver, ¿podemos hablar?`
          : `Hola ${coach.name}, mi acceso está bloqueado. ¿Podemos revisar?`,
    );
    window.location.href = `https://wa.me/${coach.phone}?text=${text}`;
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    window.location.href = '/auth';
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className={`w-full max-w-md rounded-2xl border-2 ${toneClasses} p-6 md:p-8 text-center space-y-5`}>
        <div className="flex flex-col items-center gap-3">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center ${iconBg}`}>
            <Lock className="w-7 h-7" />
          </div>
          <variant.Icon className="w-5 h-5 text-muted-foreground" />
        </div>

        <div>
          <h1 className="text-xl md:text-2xl font-black uppercase tracking-tight">
            {variant.title}
          </h1>
          <p className="text-sm mt-3 leading-relaxed">{variant.body}</p>
          <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{variant.subBody}</p>
        </div>

        <div className="space-y-2 pt-2">
          {variant.cta && coachId && (
            <button
              onClick={handleContact}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#25D366] hover:bg-[#1ebe5b] text-white font-black text-sm uppercase tracking-wider transition-colors min-h-[48px] shadow-sm"
            >
              <MessageCircle className="w-4 h-4" />
              {coach?.name ? `Contactar a ${coach.name}` : 'Contactar a tu coach'}
            </button>
          )}
          <button
            onClick={handleSignOut}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-secondary hover:bg-border text-foreground font-bold text-sm uppercase tracking-wider transition-colors min-h-[44px]"
          >
            <LogOut className="w-4 h-4" />
            Cerrar sesión
          </button>
        </div>
      </div>
    </div>
  );
}

function formatDate(iso: string): string {
  // Parse YYYY-MM-DD at noon to avoid timezone drift
  const d = new Date(iso + 'T12:00:00');
  return d.toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' });
}
